/**
 * Terrain elevation lookup for 2D stop placement.
 *
 * The 3D viewer builds its terrain from AWS "terrarium" DEM tiles
 * (`s3.amazonaws.com/elevation-tiles-prod`). A 2D Leaflet click gives only
 * lat/lon — the raster basemap carries no height — so a placed stop would float
 * above or below the terrain the viewer renders. This reads the SAME DEM
 * directly: fetch the terrarium tile for the point, decode its elevation from
 * the pixel (RGB → metres), bilinear-interpolate the four surrounding samples,
 * and cache tiles for the session.
 *
 * Because it is the same source the viewer meshes, a 2D-placed stop lands on the
 * ground exactly like a 3D-picked one. Tiles are CORS-enabled, so a canvas
 * decode works; ANY failure (network, tainted canvas) returns null and the
 * caller keeps its previous fallback.
 */

const TILE = 256;
// Terrarium data is global to z15; z14 (~9.5 m/px at the equator, finer toward
// the poles) is ample for a camera stop and universally available.
const ZOOM = 14;
const tileUrl = (z: number, x: number, y: number) =>
  `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${z}/${x}/${y}.png`;

// Decoded elevation grids, keyed "z/x/y". A promise so concurrent lookups of the
// same tile share one fetch; a resolved null marks a tile that failed to load.
const tileCache = new Map<string, Promise<Float32Array | null>>();

function lonToTileX(lon: number, z: number): number {
  return ((lon + 180) / 360) * 2 ** z;
}
function latToTileY(lat: number, z: number): number {
  const r = (lat * Math.PI) / 180;
  return ((1 - Math.asinh(Math.tan(r)) / Math.PI) / 2) * 2 ** z;
}

function loadTile(z: number, x: number, y: number): Promise<Float32Array | null> {
  const key = `${z}/${x}/${y}`;
  const hit = tileCache.get(key);
  if (hit) return hit;
  const p = new Promise<Float32Array | null>((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // terrarium sends Access-Control-Allow-Origin: *
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = TILE;
        canvas.height = TILE;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        const { data } = ctx.getImageData(0, 0, TILE, TILE);
        const grid = new Float32Array(TILE * TILE);
        for (let i = 0; i < TILE * TILE; i++) {
          const o = i * 4;
          // Terrarium decode: metres = (R*256 + G + B/256) - 32768.
          grid[i] = data[o] * 256 + data[o + 1] + data[o + 2] / 256 - 32768;
        }
        resolve(grid);
      } catch {
        resolve(null); // tainted canvas / decode failure
      }
    };
    img.onerror = () => resolve(null);
    img.src = tileUrl(z, x, y);
  });
  tileCache.set(key, p);
  return p;
}

/**
 * Terrain elevation (metres) at a WGS84 point, sampled from the viewer's
 * terrarium DEM. Resolves null if the tile can't be fetched or decoded, so the
 * caller can fall back to its previous behaviour.
 */
export async function elevationAt(lat: number, lon: number): Promise<number | null> {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  const n = 2 ** ZOOM;
  const fx = lonToTileX(lon, ZOOM);
  const fy = latToTileY(lat, ZOOM);
  const tx = Math.floor(fx);
  const ty = Math.floor(fy);
  const grid = await loadTile(ZOOM, ((tx % n) + n) % n, Math.min(Math.max(ty, 0), n - 1));
  if (!grid) return null;

  // Pixel position within the tile, shifted -0.5 so a sample sits at the pixel
  // CENTRE, then bilinear-blend the four surrounding samples (clamped to the
  // tile — cross-edge error is sub-pixel and well within a stop's tolerance).
  const px = (fx - tx) * TILE - 0.5;
  const py = (fy - ty) * TILE - 0.5;
  const x0 = Math.min(Math.max(Math.floor(px), 0), TILE - 1);
  const y0 = Math.min(Math.max(Math.floor(py), 0), TILE - 1);
  const x1 = Math.min(x0 + 1, TILE - 1);
  const y1 = Math.min(y0 + 1, TILE - 1);
  const dx = Math.min(Math.max(px - x0, 0), 1);
  const dy = Math.min(Math.max(py - y0, 0), 1);
  const at = (x: number, y: number) => grid[y * TILE + x];
  const top = at(x0, y0) * (1 - dx) + at(x1, y0) * dx;
  const bottom = at(x0, y1) * (1 - dx) + at(x1, y1) * dx;
  const elev = top * (1 - dy) + bottom * dy;
  return Number.isFinite(elev) ? elev : null;
}
